import { creditsService, CreditsBalance, CreditLedgerEntry, CreditTiersResponse } from '@/services/credits.service';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  Activity, ArrowUpRight, Check, CheckCircle2, DollarSign, Eye, EyeOff, FlaskConical, KeyRound, Languages, Loader2,
  MessageSquare, Pencil, Plus, RefreshCw, RotateCcw, Save, Sparkles, Star, Trash2, X, XCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { LoadErrorState } from '@/components/ListStates';
import { ProviderIcon } from '@/components/products/ProviderIcon';
import { SettingsCard, SettingsPage, SettingsSection, SettingsStat, SettingsToggle } from '@/components/settings/SettingsPage';
import {
  settingsService,
  AiModelsResponse,
  AiAccessSettings,
  AiProviderName,
  AiProviderEntry,
  AiProviderKey,
  AiUsageResponse,
  AiCostCapsResponse,
  LanguagePreferencesMap,
  ElevenLabsSettings,
  SavedVoice,
} from '@/services/settings.service';
import toast from 'react-hot-toast';
import { AiKeyInput } from '@/components/AiKeyInput';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { VoicePickerModal } from '@/components/VoicePickerModal';
import { useAuth } from '@/hooks/useAuth';
import { useModuleAccess } from '@/contexts/ModuleAccessContext';

const TIER_LABELS: Record<string, { label: string; className: string }> = {
  // legacy tier names
  fast:     { label: 'Fast',     className: 'bg-success-soft text-success' },
  pro:      { label: 'Pro',      className: 'bg-warning-soft text-warning' },
  // new tier names from registry
  mini:     { label: 'Mini',     className: 'bg-success-soft text-success' },
  standard: { label: 'Standard', className: 'bg-info-soft text-info' },
  flagship: { label: 'Flagship', className: 'bg-warning-soft text-warning' },
};

const relative = (iso?: string | null) => {
  if (!iso) return 'never';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${Math.max(1, m)} min ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/** Anything the server answered was already toasted by the axios interceptor, so only toast what never got a response. */
const wasToasted = (e: unknown) => !!(e as { response?: unknown })?.response;

// ── Small shared pieces ───────────────────────────────────────────────────────
function SkeletonRows({ rows = 3, rowClass = 'h-14' }: { rows?: number; rowClass?: string }) {
  return (
    <div className="space-y-2 p-5">
      {Array.from({ length: rows }, (_, i) => <div key={i} className={clsx('skeleton rounded-xl', rowClass)} />)}
    </div>
  );
}

function EmptyBlock({ icon, title, children }: { icon: React.ReactNode; title: string; children?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white shadow-soft">{icon}</div>
      <div className="text-sm font-medium text-text-primary">{title}</div>
      {children && <p className="max-w-sm text-xs text-text-muted">{children}</p>}
    </div>
  );
}

function OkPill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-success-soft px-2 py-0.5 text-2xs font-medium text-success">
      <span className="h-1.5 w-1.5 rounded-full bg-success" /> {children}
    </span>
  );
}

function IconBtn({ children, title, onClick, disabled, danger }: { children: React.ReactNode; title: string; onClick: () => void; disabled?: boolean; danger?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} aria-label={title}
      className={clsx('flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors disabled:opacity-50', danger ? 'hover:bg-danger-soft hover:text-danger' : 'hover:bg-surface-alt hover:text-text-primary')}>
      {children}
    </button>
  );
}

// ── Set / replace key dialog (admin only) ─────────────────────────────────────
function SetKeyDialog({ entry, existing, onClose, onSaved }: {
  entry: AiProviderEntry; existing: AiProviderKey | null; onClose: () => void; onSaved: () => void;
}) {
  const models = entry.models.filter((m) => !m.deprecated);
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState(existing?.defaultModel ?? models[0]?.id ?? entry.models[0]?.id ?? '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!apiKey.trim()) { toast.error('Paste the API key'); return; }
    setBusy(true);
    try {
      await settingsService.upsertAiKey({ provider: entry.id, apiKey: apiKey.trim(), defaultModel: model, isDefault: true });
      toast.success(existing ? `${entry.label} key replaced` : `${entry.label} key saved`);
      onSaved();
    } catch { /* interceptor toasted */ } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={() => !busy && onClose()} title={existing ? `Replace ${entry.label} key` : `Set ${entry.label} key`} widthClass="max-w-xl">
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded-xl border border-border bg-surface-alt/40 p-3">
          <ProviderIcon provider={entry.id} size={38} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-text-primary">{entry.label}</div>
            <div className="text-2xs text-text-muted">
              {existing ? <>Current key <span className="font-mono">{existing.keyMasked}</span> will be replaced.</> : 'Stored encrypted and used when Bring your own key is selected.'}
            </div>
          </div>
        </div>
        <FormField label="API key" required hintBelow hint={entry.consoleUrl ? `Get one at ${entry.consoleUrl}` : 'Stored encrypted at rest.'}>
          <AiKeyInput
            value={apiKey}
            onChange={setApiKey}
            placeholder="sk-… / AIza… / sk-…"
            onTest={() => settingsService.testAiKey({ provider: entry.id, apiKey: apiKey.trim(), model })}
          />
        </FormField>
        <FormField label="Fallback model" hintBelow hint="Used only when nothing else is configured. The model your team actually uses is the company default on the card.">
          <Select value={model} onChange={(e) => setModel(e.target.value)}>
            {entry.models.map((m) => (
              <option key={m.id} value={m.id}>{m.label}{m.deprecated ? ' (deprecated)' : ''}</option>
            ))}
          </Select>
        </FormField>
      </div>
      <div className="mt-5 flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={save} loading={busy} disabled={!apiKey.trim() || busy}>{existing ? 'Replace key' : 'Save key'}</Button>
      </div>
    </Modal>
  );
}

// ── AI providers: one row per provider with its key, default model and key actions ──
interface VaultProps {
  providers: AiProviderEntry[];
  keys: AiProviderKey[] | null;          // null = not loaded / not admin
  isAdmin: boolean;
  savedDefault: { provider: AiProviderName; model: string };
  selectedProvider: AiProviderName;
  drafts: Record<string, string>;
  onDraftModel: (provider: AiProviderName, model: string) => void;
  onMakeDefault: (provider: AiProviderName) => void;
  onKeysChanged: () => void;
}

function ProvidersVault({ providers, keys, isAdmin, savedDefault, selectedProvider, drafts, onDraftModel, onMakeDefault, onKeysChanged }: VaultProps) {
  const [editing, setEditing] = useState<{ entry: AiProviderEntry; existing: AiProviderKey | null } | null>(null);
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const keysByProvider = useMemo(() => {
    const m = new Map<AiProviderName, AiProviderKey[]>();
    for (const k of keys ?? []) m.set(k.provider, [...(m.get(k.provider) ?? []), k]);
    return m;
  }, [keys]);
  // The key the backend will actually use for a provider: its isDefault one, else the first.
  const primaryKey = (p: AiProviderName): AiProviderKey | null => {
    const list = keysByProvider.get(p) ?? [];
    return list.find((k) => k.isDefault) ?? list[0] ?? null;
  };

  const onReveal = async (k: AiProviderKey) => {
    if (revealed[k._id]) { setRevealed(({ [k._id]: _, ...rest }) => rest); return; }
    setBusy(`reveal:${k._id}`);
    try { const r = await settingsService.revealAiKey(k._id); setRevealed((m) => ({ ...m, [k._id]: r.apiKey })); }
    catch (e) { if (!wasToasted(e)) toast.error('Failed to reveal key'); }
    finally { setBusy(null); }
  };
  const onTest = async (k: AiProviderKey) => {
    setBusy(`test:${k._id}`);
    try {
      const r = await settingsService.testStoredAiKey(k._id);
      if (r.ok) toast.success('Key is working'); else toast.error(`Test failed: ${(r as { ok: false; error: string }).error}`);
      onKeysChanged();
    } catch { /* interceptor toasted */ } finally { setBusy(null); }
  };
  const onRemove = async (entry: AiProviderEntry, k: AiProviderKey) => {
    if (!window.confirm(`Remove the ${entry.label} key ${k.keyMasked}? BYOK requests for this provider will stop until you add another key or explicitly select Managed AI.`)) return;
    setBusy(k._id);
    try { await settingsService.removeAiKey(k._id); toast.success('Key removed'); onKeysChanged(); }
    catch { /* interceptor toasted */ } finally { setBusy(null); }
  };

  const keyCount = keys?.length ?? 0;
  const noneAvailable = providers.every((p) => !p.available);

  const statusPill = (entry: AiProviderEntry, k: AiProviderKey | null) => {
    if (k) return <OkPill>Company key</OkPill>;
    if (entry.available) {
      return <span className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs font-medium text-text-muted">Platform default</span>;
    }
    return <span className="rounded-full bg-warning-soft px-2 py-0.5 text-2xs font-medium text-warning">Not configured</span>;
  };

  const defaultBadge = (entry: AiProviderEntry) => {
    const isSaved = savedDefault.provider === entry.id;
    const isSelected = selectedProvider === entry.id;
    if (isSaved && isSelected) return <span className="rounded-full bg-gold-gradient px-2 py-0.5 text-2xs font-bold tracking-wide text-on-primary">Default</span>;
    if (isSelected) return <span className="rounded-full bg-warning-soft px-2 py-0.5 text-2xs font-bold tracking-wide text-warning">Default (unsaved)</span>;
    if (isSaved) return <span className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs font-bold tracking-wide text-text-muted line-through">Default</span>;
    return null;
  };

  const keyActions = (entry: AiProviderEntry, k: AiProviderKey | null) => (
    <div className="flex items-center">
      {k && (
        <>
          <IconBtn title="Test key" onClick={() => onTest(k)} disabled={!!busy}>{busy === `test:${k._id}` ? <Loader2 size={14} className="animate-spin" /> : <FlaskConical size={14} />}</IconBtn>
          <IconBtn title={revealed[k._id] ? 'Hide key' : 'Reveal key'} onClick={() => onReveal(k)} disabled={!!busy}>{revealed[k._id] ? <EyeOff size={14} /> : <Eye size={14} />}</IconBtn>
        </>
      )}
      <IconBtn title={k ? 'Replace key' : 'Set key'} onClick={() => setEditing({ entry, existing: k })} disabled={!!busy}>{k ? <Pencil size={14} /> : <KeyRound size={14} />}</IconBtn>
      {k && <IconBtn title="Remove key" danger onClick={() => onRemove(entry, k)} disabled={!!busy}><Trash2 size={14} /></IconBtn>}
    </div>
  );

  const testLine = (k: AiProviderKey) => k.lastTestedAt && k.lastTestResult ? (
    <span className={clsx('inline-flex items-center gap-1 text-2xs', k.lastTestResult === 'ok' ? 'text-success' : 'text-danger')}>
      {k.lastTestResult === 'ok' ? <CheckCircle2 size={11} /> : <XCircle size={11} />} {k.lastTestResult === 'ok' ? 'Key works' : 'Test failed'} · {relative(k.lastTestedAt)}
    </span>
  ) : null;

  if (providers.length === 0) {
    return <EmptyBlock icon={<Sparkles size={20} />} title="No AI providers found">Providers will show up here as soon as the platform offers them.</EmptyBlock>;
  }

  return (
    <>
      <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3 text-2xs text-text-muted">
        <span>{providers.length} provider{providers.length === 1 ? '' : 's'}</span>
        {isAdmin && keys && <span>{keyCount} company key{keyCount === 1 ? '' : 's'}</span>}
      </div>

      {noneAvailable && !isAdmin && (
        <div className="border-b border-border bg-warning-soft px-5 py-3 text-xs text-text-secondary">
          No AI providers are available. Ask a company admin to add a provider key.
        </div>
      )}

      <ul className="divide-y divide-border">
        {providers.map((entry) => {
          const k = primaryKey(entry.id);
          const extra = (keysByProvider.get(entry.id) ?? []).filter((x) => x._id !== k?._id);
          const isSelected = selectedProvider === entry.id;
          const value = drafts[entry.id] ?? entry.models[0]?.id ?? '';
          const chosen = entry.models.find((m) => m.id === value);
          const tier = chosen ? (TIER_LABELS[chosen.tier] ?? TIER_LABELS.standard) : null;
          return (
            <li key={entry.id} className={clsx('flex items-start gap-3 px-5 py-4 transition-colors', isSelected && 'bg-primary-soft/40')}>
              <ProviderIcon provider={entry.id} size={40} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-sm font-semibold text-text-primary">{entry.label}</span>
                  {defaultBadge(entry)}
                  {statusPill(entry, k)}
                </div>

                {k ? (
                  <div className="mt-1.5 space-y-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-2xs">
                      <span className="min-w-0 break-all text-text-primary">{revealed[k._id] ?? k.keyMasked}</span>
                      <span className="text-text-muted">fallback {k.defaultModel}</span>
                      {!k.isActive && <span className="rounded bg-surface-alt px-1.5 py-0.5 text-text-muted">inactive</span>}
                    </div>
                    {testLine(k)}
                  </div>
                ) : (
                  <div className="mt-1.5 text-2xs text-text-muted">
                    {entry.available ? 'No company key. This provider is available.' : 'No key yet. Add one to turn this provider on.'}
                  </div>
                )}

                {extra.map((x) => (
                  <div key={x._id} className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-dashed border-border px-2.5 py-1 font-mono text-2xs text-text-muted">
                    <span className="min-w-0 break-all">{revealed[x._id] ?? x.keyMasked} · fallback {x.defaultModel}{!x.isActive && ' · inactive'}</span>
                    {isAdmin && keyActions(entry, x)}
                  </div>
                ))}

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <div className="flex min-w-0 flex-1 basis-64 items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <Select
                        value={value}
                        disabled={!entry.available || !isAdmin}
                        onChange={(e) => onDraftModel(entry.id, e.target.value)}
                        className="text-sm"
                        aria-label={`${entry.label} default model`}
                      >
                        {entry.models.map((m) => (
                          <option key={m.id} value={m.id}>{m.label}{m.deprecated ? ' (deprecated)' : ''}</option>
                        ))}
                      </Select>
                    </div>
                    {tier && (
                      <span className={clsx('shrink-0 rounded-full px-2 py-0.5 text-2xs font-semibold uppercase tracking-wide', tier.className)}>
                        {tier.label}
                      </span>
                    )}
                  </div>
                  {!isSelected && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!entry.available || !isAdmin}
                      onClick={() => onMakeDefault(entry.id)}
                      iconLeft={<Star size={12} />}
                    >
                      Make default
                    </Button>
                  )}
                  {isAdmin && keyActions(entry, k)}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {editing && (
        <SetKeyDialog
          entry={editing.entry}
          existing={editing.existing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onKeysChanged(); }}
        />
      )}
    </>
  );
}

// ── ElevenLabs voiceover key (admin only) ─────────────────────────────────────
// Powers AI Studio voiceover generation. Key + voice live on CompanyAiSettings
// (the chat-provider key list above has a closed provider enum).

function ElevenLabsCard() {
  const [data, setData] = useState<ElevenLabsSettings | null>(null);
  const [failed, setFailed] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [voices, setVoices] = useState<SavedVoice[]>([]);
  const [defaultVoiceId, setDefaultVoiceId] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const refresh = useCallback(() => {
    setFailed(false);
    return settingsService.getElevenLabs()
      .then((r) => { setData(r); setVoices(r.voices ?? []); setDefaultVoiceId(r.voiceId ?? ''); })
      .catch(() => setFailed(true));
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);

  const saveKey = async () => {
    if (!apiKey.trim()) return;
    setSaving(true);
    try {
      const r = await settingsService.setElevenLabs({ apiKey });
      setData(r); setApiKey('');
      toast.success('ElevenLabs key saved. Now pick your voices.');
    } catch { /* interceptor toasted */ } finally { setSaving(false); }
  };

  const testKey = async () => {
    if (!apiKey.trim()) { toast.error('Paste a key to test'); return; }
    setTesting(true);
    try {
      const r = await settingsService.testElevenLabsKey(apiKey.trim());
      if (r.ok) toast.success('Key is valid'); else toast.error(r.error ?? 'Key is invalid');
    } catch { /* interceptor toasted */ } finally { setTesting(false); }
  };

  const persistVoices = async (next: SavedVoice[], nextDefault: string) => {
    setSaving(true);
    try {
      const r = await settingsService.setElevenLabs({
        voices: next,
        voiceId: nextDefault || null,
      });
      setData(r); setVoices(r.voices ?? []); setDefaultVoiceId(r.voiceId ?? '');
      toast.success('Voices saved');
    } catch { /* interceptor toasted */ } finally { setSaving(false); }
  };

  const onPicked = (picked: SavedVoice[]) => {
    // Keep a valid default — first picked voice if the current default dropped out.
    const nextDefault = picked.some((v) => v.voiceId === defaultVoiceId)
      ? defaultVoiceId
      : (picked[0]?.voiceId ?? '');
    persistVoices(picked, nextDefault);
  };

  const removeKey = async () => {
    if (!window.confirm('Remove the ElevenLabs key? AI Studio voiceover will be disabled.')) return;
    setSaving(true);
    try {
      const r = await settingsService.setElevenLabs({ apiKey: null });
      setData(r); setVoices([]); setDefaultVoiceId('');
      toast.success('ElevenLabs key removed');
    } catch { /* interceptor toasted */ } finally { setSaving(false); }
  };

  if (!data) {
    return (
      <SettingsCard padded={false}>
        {failed
          ? <LoadErrorState bare what="your voice settings" onRetry={() => void refresh()} />
          : <SkeletonRows rows={2} rowClass="h-16" />}
      </SettingsCard>
    );
  }

  if (!data.configured) {
    return (
      <SettingsCard
        footer={(
          <>
            <Button size="sm" variant="ghost" onClick={testKey} loading={testing} disabled={!apiKey.trim()}>Test key</Button>
            <Button size="sm" onClick={saveKey} loading={saving} disabled={!apiKey.trim()}>Save key</Button>
          </>
        )}
      >
        <FormField label="API key" hintBelow hint="Get one at elevenlabs.io under Profile, then API keys.">
          <AiKeyInput value={apiKey} onChange={setApiKey} placeholder="sk_…" />
        </FormField>
      </SettingsCard>
    );
  }

  return (
    <SettingsCard padded={false}>
      <div className="divide-y divide-border">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-5 py-4">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-alt text-text-secondary"><KeyRound size={16} /></span>
          <div className="min-w-0 flex-1 basis-40">
            <div className="text-sm font-medium text-text-primary">API key</div>
            <div className="truncate font-mono text-2xs text-text-muted">{data.keyMasked}</div>
          </div>
          <OkPill>Configured</OkPill>
          <IconBtn title="Remove key" danger onClick={removeKey} disabled={saving}><Trash2 size={14} /></IconBtn>
        </div>

        <div className="px-5 py-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-text-primary">Your voices</div>
              <div className="text-2xs text-text-muted">Pick up to 10 from your ElevenLabs library.</div>
            </div>
            <Button size="sm" variant="secondary" onClick={() => setPickerOpen(true)}>
              {voices.length > 0 ? 'Edit voices' : 'Browse & pick voices'}
            </Button>
          </div>
          {voices.length === 0 ? (
            <p className="mt-3 rounded-lg border border-dashed border-border bg-surface-alt/40 px-3 py-3 text-xs text-text-muted">
              No voices picked yet. Choose <strong>Browse &amp; pick voices</strong> to add some. You will pick one each time you make a voiceover.
            </p>
          ) : (
            <div className="mt-3 space-y-2">
              <div className="flex flex-wrap gap-1.5">
                {voices.map((v) => (
                  <button
                    key={v.voiceId}
                    type="button"
                    disabled={saving}
                    onClick={() => persistVoices(voices, v.voiceId)}
                    title={defaultVoiceId === v.voiceId ? 'Default voice' : 'Set as default'}
                    className={clsx(
                      'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors disabled:opacity-60',
                      defaultVoiceId === v.voiceId
                        ? 'border-primary bg-primary-soft text-primary'
                        : 'border-border bg-surface text-text-secondary hover:border-primary/50',
                    )}
                  >
                    {defaultVoiceId === v.voiceId && <Star size={10} className="fill-current" />}
                    {v.name}{v.accent ? <span className="opacity-60"> · {v.accent}</span> : null}
                  </button>
                ))}
              </div>
              <p className="text-2xs text-text-muted">The starred voice is the default, used when a voiceover does not pick one. Click a voice to make it the default.</p>
            </div>
          )}
        </div>
      </div>

      <VoicePickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        initialSelected={voices}
        onSave={onPicked}
      />
    </SettingsCard>
  );
}

// ── WhatsApp AI fallback ─────────────────────────────────────────────────────

function WhatsappAiFallbackSections({ isAdmin }: { isAdmin: boolean }) {
  const [enabled, setEnabled] = useState(false);
  const [maxTurns, setMaxTurns] = useState(12);
  const [savedMaxTurns, setSavedMaxTurns] = useState(12);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    settingsService
      .getWhatsappAiFallback()
      .then((r) => { setEnabled(r.enabled); setMaxTurns(r.maxTurns); setSavedMaxTurns(r.maxTurns); })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const onToggle = async (next: boolean) => {
    setSaving(true);
    const prev = enabled;
    setEnabled(next);
    try {
      const r = await settingsService.setWhatsappAiFallback({ enabled: next });
      setEnabled(r.enabled);
      toast.success(next ? 'AI auto-replies enabled' : 'AI auto-replies disabled');
    } catch (e: unknown) {
      setEnabled(prev);
      if (!wasToasted(e)) toast.error((e as { message?: string })?.message ?? 'Failed to save');
    } finally { setSaving(false); }
  };

  const turnsInvalid = maxTurns < 1 || maxTurns > 50;
  const turnsDirty = maxTurns !== savedMaxTurns;

  const onSaveTurns = async () => {
    if (turnsInvalid) return;
    setSaving(true);
    try {
      await settingsService.setWhatsappAiFallback({ maxTurns });
      setSavedMaxTurns(maxTurns);
      toast.success('Reply limit saved');
    } catch (e) { if (!wasToasted(e)) toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const adminNote = isAdmin ? null : ' Only a company admin can change this.';

  if (failed) {
    return (
      <SettingsSection title="AI auto-replies" description="A company-wide safety net for leads that have no scripted flow attached.">
        <SettingsCard padded={false}>
          <LoadErrorState bare what="the WhatsApp fallback settings" onRetry={load} />
        </SettingsCard>
      </SettingsSection>
    );
  }

  return (
    <>
      <SettingsSection
        title="AI auto-replies"
        description={<>A company-wide safety net for leads that have no scripted flow attached.{adminNote}</>}
        aside={(
          <p className="text-xs leading-relaxed text-text-muted">
            Want a number to be fully AI, with no flows? Set its Bot mode to AI chat under{' '}
            <Link to="/whatsapp-account" className="font-medium text-primary hover:underline">WhatsApp → Account settings</Link>.
          </p>
        )}
      >
        <SettingsCard padded={false}>
          {loading ? (
            <SkeletonRows rows={1} rowClass="h-16" />
          ) : (
            <SettingsToggle
              icon={<MessageSquare size={16} />}
              label="Reply with AI when no flow matches"
              description={(
                <>
                  Uses your default AI model. Fill in your{' '}
                  <Link to="/settings/business-profile" className="font-medium text-primary hover:underline">business profile</Link>
                  : the AI reads it before every reply.
                </>
              )}
              checked={enabled}
              disabled={!isAdmin || loading || saving}
              onChange={onToggle}
              badge={enabled ? <OkPill>Active</OkPill> : undefined}
            />
          )}
        </SettingsCard>
      </SettingsSection>

      <SettingsSection
        title="Reply limit"
        description="After this many AI replies in a row with no teammate stepping in, the bot pauses so a person can take over."
      >
        <SettingsCard
          footer={isAdmin && !loading && turnsDirty ? (
            <>
              <Button variant="ghost" size="sm" iconLeft={<RotateCcw size={14} />} disabled={saving} onClick={() => setMaxTurns(savedMaxTurns)}>Discard</Button>
              <Button size="sm" iconLeft={<Save size={14} />} loading={saving} disabled={turnsInvalid} onClick={onSaveTurns}>Save limit</Button>
            </>
          ) : undefined}
        >
          {loading ? (
            <div className="skeleton h-16 rounded-xl" />
          ) : (
            <FormField
              label="Max AI replies per conversation"
              htmlFor="ai-fallback-max-turns"
              hintBelow
              error={turnsInvalid ? 'Enter a number from 1 to 50.' : undefined}
              hint={enabled ? 'From 1 to 50.' : 'Turn on AI auto-replies to change this.'}
            >
              <div className="w-32">
                <Input
                  id="ai-fallback-max-turns"
                  type="number"
                  min={1}
                  max={50}
                  value={maxTurns}
                  disabled={!isAdmin || !enabled || saving}
                  invalid={turnsInvalid}
                  onChange={(e) => setMaxTurns(Number(e.target.value))}
                />
              </div>
            </FormField>
          )}
        </SettingsCard>
      </SettingsSection>
    </>
  );
}

// ── Per-language provider routing ────────────────────────────────────────────
// Detected inbound language → provider/model override for the WhatsApp AI
// fallback. Misses fall back to the company default. Quality-driven defaults:
// Arabic → Qwen, CJK → Qwen/Gemini, others → OpenAI.

const LANGUAGE_OPTIONS: Array<{ code: string; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'Arabic' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'zh', label: 'Chinese' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'hi', label: 'Hindi' },
  { code: 'ru', label: 'Russian' },
  { code: 'he', label: 'Hebrew' },
  { code: 'th', label: 'Thai' },
];

function LanguagePreferencesCard({ providers, isAdmin }: { providers: AiProviderEntry[]; isAdmin: boolean }) {
  const [prefs, setPrefs] = useState<LanguagePreferencesMap>({});
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftLang, setDraftLang] = useState('ar');

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    settingsService
      .getLanguagePreferences()
      .then(setPrefs)
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const availableProviders = providers.filter((p) => p.available);
  const usedLangs = new Set(Object.keys(prefs));
  const addableLangs = LANGUAGE_OPTIONS.filter((l) => !usedLangs.has(l.code));
  // The remembered pick may already be in the list (e.g. Arabic loaded from the server): fall back to the first free
  // language so the dropdown and "Add language" always agree.
  const pickedLang = addableLangs.some((l) => l.code === draftLang) ? draftLang : (addableLangs[0]?.code ?? '');

  const persist = async (next: LanguagePreferencesMap) => {
    setSaving(true);
    const prev = prefs;
    setPrefs(next);
    try {
      const r = await settingsService.setLanguagePreferences(next);
      setPrefs(r);
      toast.success('Language preferences saved');
    } catch (e: unknown) {
      setPrefs(prev);
      if (!wasToasted(e)) toast.error((e as { message?: string })?.message ?? 'Failed to save');
    } finally { setSaving(false); }
  };

  const onAdd = () => {
    if (!pickedLang || prefs[pickedLang]) return;
    const firstProvider = availableProviders[0]?.id ?? 'openai';
    const next = { ...prefs, [pickedLang]: { provider: firstProvider } };
    void persist(next);
    if (addableLangs.length > 1) {
      const remaining = addableLangs.filter((l) => l.code !== pickedLang);
      if (remaining[0]) setDraftLang(remaining[0].code);
    }
  };

  const onRemove = (code: string) => {
    const next = { ...prefs };
    delete next[code];
    void persist(next);
  };

  const onProviderChange = (code: string, provider: AiProviderName) => {
    const next = { ...prefs, [code]: { provider } };
    void persist(next);
  };

  const onModelChange = (code: string, model: string) => {
    const current = prefs[code];
    if (!current) return;
    const next = {
      ...prefs,
      [code]: model ? { provider: current.provider, model } : { provider: current.provider },
    };
    void persist(next);
  };

  const codes = Object.keys(prefs);

  return (
    <SettingsCard
      padded={false}
      footer={isAdmin && !loading && !failed && addableLangs.length > 0 ? (
        <>
          {availableProviders.length === 0 && <span className="mr-auto text-xs text-text-muted">Add a provider key first.</span>}
          <div className="w-44 max-w-full">
            <Select
              value={pickedLang}
              onChange={(e) => setDraftLang(e.target.value)}
              disabled={saving}
              className="text-sm"
              aria-label="Language to add"
            >
              {addableLangs.map((l) => (
                <option key={l.code} value={l.code}>{l.label} ({l.code})</option>
              ))}
            </Select>
          </div>
          <Button
            size="sm"
            variant="secondary"
            className="shrink-0"
            onClick={onAdd}
            disabled={saving || availableProviders.length === 0}
            iconLeft={<Plus size={14} />}
          >
            Add language
          </Button>
        </>
      ) : undefined}
    >
      {failed ? (
        <LoadErrorState bare what="your language settings" onRetry={load} />
      ) : loading ? (
        <SkeletonRows rows={2} rowClass="h-24" />
      ) : codes.length === 0 ? (
        <EmptyBlock icon={<Languages size={20} />} title="Every language uses the default model">
          {isAdmin ? 'Add a language below to answer it with a different provider or model.' : 'A company admin can route a language to a different provider or model.'}
        </EmptyBlock>
      ) : (
        <ul className="divide-y divide-border">
          {Object.entries(prefs).map(([code, val]) => {
            const provider = providers.find((p) => p.id === val.provider);
            const langLabel = LANGUAGE_OPTIONS.find((l) => l.code === code)?.label ?? code.toUpperCase();
            return (
              <li key={code} className="flex items-start gap-3 px-5 py-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gold-gradient-soft text-xs font-bold text-primary">
                  {code.toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-text-primary">{langLabel}</div>
                  <div className="mt-2 grid grid-cols-[repeat(auto-fit,minmax(10.5rem,1fr))] gap-2">
                    <Select
                      value={val.provider}
                      onChange={(e) => onProviderChange(code, e.target.value as AiProviderName)}
                      disabled={saving || !isAdmin}
                      className="text-sm"
                      aria-label={`${langLabel} provider`}
                    >
                      {!availableProviders.some((p) => p.id === val.provider) && (
                        <option value={val.provider}>{provider?.label ?? val.provider} (unavailable)</option>
                      )}
                      {availableProviders.map((p) => (
                        <option key={p.id} value={p.id}>{p.label}</option>
                      ))}
                    </Select>
                    <Select
                      value={val.model ?? ''}
                      onChange={(e) => onModelChange(code, e.target.value)}
                      disabled={saving || !provider || !isAdmin}
                      className="text-sm"
                      aria-label={`${langLabel} model`}
                    >
                      <option value="">(provider default)</option>
                      {provider?.models.filter((m) => !m.deprecated).map((m) => (
                        <option key={m.id} value={m.id}>{m.label}</option>
                      ))}
                    </Select>
                  </div>
                </div>
                {isAdmin && (
                  <IconBtn title={`Remove ${langLabel}`} danger onClick={() => onRemove(code)} disabled={saving}><X size={15} /></IconBtn>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </SettingsCard>
  );
}

// ── AI cost caps ─────────────────────────────────────────────────────────────

const capText = (v: number | null) => (v === null ? '' : String(v));

function CostCapsSection({ isAdmin }: { isAdmin: boolean }) {
  const [data, setData] = useState<AiCostCapsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dailyCap, setDailyCap] = useState<string>('');
  const [monthlyCap, setMonthlyCap] = useState<string>('');

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    settingsService.getAiCostCaps()
      .then((r) => { setData(r); setDailyCap(capText(r.daily.capUsd)); setMonthlyCap(capText(r.monthly.capUsd)); })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const onSave = async () => {
    setSaving(true);
    try {
      const patch: { daily?: number | null; monthly?: number | null } = {};
      patch.daily = dailyCap === '' ? null : Number(dailyCap);
      patch.monthly = monthlyCap === '' ? null : Number(monthlyCap);
      const r = await settingsService.setAiCostCaps(patch);
      setData(r);
      setDailyCap(capText(r.daily.capUsd));
      setMonthlyCap(capText(r.monthly.capUsd));
      toast.success('Cost caps saved');
    } catch (e: unknown) {
      if (!wasToasted(e)) toast.error('Save failed');
    } finally { setSaving(false); }
  };

  const description = `Hard ceilings on AI spend. When one is reached, the WhatsApp AI bot pauses replies until the next reset.${isAdmin ? '' : ' Only a company admin can change them.'}`;

  if (failed || loading || !data) {
    return (
      <SettingsSection title="Spending limits" description={description}>
        <SettingsCard padded={false}>
          {failed
            ? <LoadErrorState bare what="your spending limits" onRetry={load} />
            : <SkeletonRows rows={2} rowClass="h-20" />}
        </SettingsCard>
      </SettingsSection>
    );
  }

  const dailyHit = data.daily.capUsd !== null && data.daily.spentUsd >= data.daily.capUsd;
  const monthlyHit = data.monthly.capUsd !== null && data.monthly.spentUsd >= data.monthly.capUsd;
  const anyHit = dailyHit || monthlyHit;

  const dailyPct = data.daily.capUsd && data.daily.capUsd > 0
    ? Math.min(100, Math.round((data.daily.spentUsd / data.daily.capUsd) * 100))
    : 0;
  const monthlyPct = data.monthly.capUsd && data.monthly.capUsd > 0
    ? Math.min(100, Math.round((data.monthly.spentUsd / data.monthly.capUsd) * 100))
    : 0;

  const dirty = dailyCap !== capText(data.daily.capUsd) || monthlyCap !== capText(data.monthly.capUsd);
  const discard = () => { setDailyCap(capText(data.daily.capUsd)); setMonthlyCap(capText(data.monthly.capUsd)); };

  return (
    <SettingsSection title="Spending limits" description={description}>
      {anyHit && (
        <div role="alert" className="rounded-xl border border-warning/40 bg-warning-soft p-3 text-xs text-warning">
          <strong>AI auto-replies are paused.</strong>{' '}
          {dailyHit
            ? `Daily cap of $${data.daily.capUsd?.toFixed(2)} reached. Resets ${new Date(data.daily.resetAt).toLocaleString()}.`
            : `Monthly cap of $${data.monthly.capUsd?.toFixed(2)} reached. Resets ${new Date(data.monthly.resetAt).toLocaleString()}.`}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <SettingsStat
          label="Spent today"
          value={`$${data.daily.spentUsd.toFixed(4)}`}
          tone={dailyHit ? 'warning' : 'default'}
          hint={data.daily.capUsd !== null ? `of $${data.daily.capUsd.toFixed(2)} cap` : 'No daily cap'}
        />
        <SettingsStat
          label="Spent this month"
          value={`$${data.monthly.spentUsd.toFixed(4)}`}
          tone={monthlyHit ? 'warning' : 'default'}
          hint={data.monthly.capUsd !== null ? `of $${data.monthly.capUsd.toFixed(2)} cap` : 'No monthly cap'}
        />
      </div>

      <SettingsCard
        footer={isAdmin && dirty ? (
          <>
            <Button variant="ghost" size="sm" iconLeft={<RotateCcw size={14} />} disabled={saving} onClick={discard}>Discard</Button>
            <Button size="sm" iconLeft={<Save size={14} />} loading={saving} onClick={onSave}>Save caps</Button>
          </>
        ) : undefined}
      >
        <div className="grid grid-cols-[repeat(auto-fit,minmax(12rem,1fr))] gap-x-4">
          <FormField label="Daily cap (USD)" htmlFor="ai-daily-cap" hintBelow hint="Leave blank for no cap.">
            <Input
              id="ai-daily-cap"
              type="number"
              min={0}
              step={0.01}
              placeholder="e.g. 5.00"
              value={dailyCap}
              disabled={!isAdmin}
              onChange={(e) => setDailyCap(e.target.value)}
              iconLeft={<DollarSign size={14} />}
            />
            {data.daily.capUsd !== null && data.daily.capUsd > 0 && (
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-alt">
                <div className={clsx('h-full rounded-full', dailyHit ? 'bg-warning' : 'bg-gold-gradient')} style={{ width: `${dailyPct}%` }} />
              </div>
            )}
          </FormField>

          <FormField label="Monthly cap (USD)" htmlFor="ai-monthly-cap" hintBelow hint="Leave blank for no cap.">
            <Input
              id="ai-monthly-cap"
              type="number"
              min={0}
              step={1}
              placeholder="e.g. 100.00"
              value={monthlyCap}
              disabled={!isAdmin}
              onChange={(e) => setMonthlyCap(e.target.value)}
              iconLeft={<DollarSign size={14} />}
            />
            {data.monthly.capUsd !== null && data.monthly.capUsd > 0 && (
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-alt">
                <div className={clsx('h-full rounded-full', monthlyHit ? 'bg-warning' : 'bg-gold-gradient')} style={{ width: `${monthlyPct}%` }} />
              </div>
            )}
          </FormField>
        </div>
      </SettingsCard>
    </SettingsSection>
  );
}

// ── Credits wallet + ledger ──────────────────────────────────────────────────

const TABLE_HEAD = 'bg-surface-alt/50 text-left text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted';

function CreditsSections() {
  const { user } = useAuth();
  const [balance, setBalance] = useState<CreditsBalance | null>(null);
  const [tiers, setTiers] = useState<CreditTiersResponse | null>(null);
  const [history, setHistory] = useState<{ rows: CreditLedgerEntry[]; total: number; page: number; pageSize: number } | null>(null);
  const [page, setPage] = useState(1);
  const [error, setError] = useState(false);
  const [breakdown, setBreakdown] = useState<'feature' | 'day'>('feature');
  const [showHistory, setShowHistory] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const load = useCallback(() => {
    setError(false);
    Promise.all([creditsService.balance(), creditsService.ledger(page), creditsService.tiers()]).then(([wallet, ledger, options]) => { setBalance(wallet); setHistory(ledger); setTiers(options); }).catch(() => setError(true));
  }, [page]);
  useEffect(load, [load]);
  const checkout = async (priceId: string) => { setRedirecting(true); try { const result = await creditsService.checkout(priceId); window.location.href = result.url; } catch { setRedirecting(false); } };
  const rows = balance ? breakdown === 'feature' ? balance.byFeature : balance.byDay : [];
  const isAdmin = user?.role === 'company_admin';

  return (
    <>
      <SettingsSection
        title="Credits"
        description="Managed AI uses your monthly allowance and granted credits. Your own provider keys are billed by the provider, not from here."
        aside={<Button size="sm" variant="secondary" iconLeft={<RefreshCw size={13} />} onClick={load}>Refresh</Button>}
      >
        {!balance ? (
          <SettingsCard padded={false}>
            {error ? <LoadErrorState bare what="your credit wallet" onRetry={load} /> : <SkeletonRows rows={3} />}
          </SettingsCard>
        ) : (
          <>
            {error && <p role="alert" className="text-xs text-danger">The wallet could not be refreshed. Try again.</p>}

            <div className="grid grid-cols-2 gap-3 @5xl:grid-cols-4">
              <SettingsStat label="Available credits" value={balance.balance.toLocaleString()} tone={balance.warningLevel === 'normal' ? 'gold' : 'warning'} />
              <SettingsStat label="Included each month" value={balance.includedMonthlyCredits.toLocaleString()} />
              <SettingsStat label="AI use this month" value={balance.used.toLocaleString()} />
              <SettingsStat label="Reserved by requests" value={balance.reserved.toLocaleString()} hint="Temporary holds" />
            </div>

            <SettingsCard>
              <div className="flex items-baseline justify-between gap-3 text-xs text-text-muted">
                <span>{balance.period} · included allowance used or reserved</span>
                <span className="font-semibold tabular-nums text-text-secondary">{balance.usedPercent}%</span>
              </div>
              <div
                role="progressbar"
                aria-label="Monthly included credits"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={balance.usedPercent}
                className="mt-2 h-2 overflow-hidden rounded-full bg-surface-alt"
              >
                <div
                  className={clsx('h-full rounded-full', balance.warningLevel === 'normal' ? 'bg-gold-gradient' : 'bg-warning')}
                  style={{ width: `${Math.min(100, balance.usedPercent)}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-text-muted">{balance.grantedAvailable.toLocaleString()} granted credits available. Grants carry forward; included credits renew monthly in UTC.</p>
              {balance.warningLevel !== 'normal' && (
                <p className="mt-3 rounded-lg bg-warning-soft p-3 text-xs text-warning">
                  {balance.debtCredits > 0
                    ? `${balance.debtCredits.toLocaleString()} credits need to be covered because actual usage exceeded its estimate. Managed AI is stopped; contact your administrator.`
                    : balance.warningLevel === 'exhausted'
                      ? <>Managed credits are exhausted. Contact your administrator for a grant, or use your own key under <Link to="/settings/ai" className="font-medium underline">Providers &amp; models</Link>.</>
                      : `${balance.usedPercent}% of the included allowance is used or reserved. Additional granted credits remain available.`}
                </p>
              )}
            </SettingsCard>

            <SettingsCard
              padded={false}
              footer={(
                <>
                  <Button size="sm" variant="ghost" className="mr-auto" onClick={() => setShowHistory((v) => !v)}>{showHistory ? 'Hide' : 'View'} credit ledger</Button>
                  {isAdmin && tiers?.enabled && tiers.tiers.map((tier) => (
                    <Button key={tier.priceId} size="sm" loading={redirecting} onClick={() => checkout(tier.priceId)}>Buy {tier.credits.toLocaleString()} · ${tier.amountUsd}</Button>
                  ))}
                </>
              )}
            >
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
                <h3 className="text-sm font-semibold text-text-primary">Credit usage</h3>
                <div className="w-36">
                  <Select aria-label="Credit breakdown" value={breakdown} onChange={(e) => setBreakdown(e.target.value as typeof breakdown)}>
                    <option value="feature">By feature</option>
                    <option value="day">By day</option>
                  </Select>
                </div>
              </div>
              {rows.length ? (
                <ul className="max-h-56 divide-y divide-border overflow-y-auto">
                  {rows.map((row) => (
                    <li key={row._id} className="flex items-center justify-between gap-3 px-5 py-2.5 text-xs">
                      <span className="min-w-0 truncate capitalize text-text-secondary">{String(row._id).replace(/_/g, ' ')}</span>
                      <span className="shrink-0 font-medium tabular-nums text-text-primary">{row.credits.toLocaleString()} credits</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-5 py-8 text-center text-xs text-text-muted">No managed credit usage this month.</p>
              )}
            </SettingsCard>
          </>
        )}
      </SettingsSection>

      {showHistory && history && (
        <div className="py-7">
          <div className="mb-3">
            <h2 className="text-sm font-semibold text-text-primary">Credit ledger</h2>
            <p className="mt-1 text-xs text-text-muted">Reservations are temporary holds; settlements are actual usage.</p>
          </div>
          <SettingsCard
            padded={false}
            footer={(
              <>
                <span className="mr-auto text-xs text-text-muted">Page {page}</span>
                <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => setPage((v) => v - 1)}>Previous</Button>
                <Button variant="ghost" size="sm" disabled={page * history.pageSize >= history.total} onClick={() => setPage((v) => v + 1)}>Next</Button>
              </>
            )}
          >
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-xs">
                <thead>
                  <tr className={clsx(TABLE_HEAD, 'border-b border-border')}>
                    <th className="px-5 py-2.5 font-semibold">Date</th>
                    <th className="px-3 py-2.5 font-semibold">Action</th>
                    <th className="px-3 py-2.5 font-semibold">Credits</th>
                    <th className="px-5 py-2.5 font-semibold">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {history.rows.length === 0 ? (
                    <tr><td colSpan={4} className="px-5 py-10 text-center text-text-muted">No credit activity yet.</td></tr>
                  ) : history.rows.map((row) => (
                    <tr key={row.entryUid} className="border-b border-border last:border-b-0 hover:bg-surface-alt/60">
                      <td className="whitespace-nowrap px-5 py-3 text-text-secondary">{new Date(row.createdAt).toLocaleDateString()}</td>
                      <td className="px-3 py-3 capitalize text-text-primary">{row.kind}</td>
                      <td className={clsx('whitespace-nowrap px-3 py-3 font-medium tabular-nums', row.credits > 0 ? 'text-success' : 'text-text-primary')}>
                        {row.credits > 0 ? '+' : ''}{row.credits.toLocaleString()}
                      </td>
                      <td className="px-5 py-3 text-text-muted">{row.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SettingsCard>
        </div>
      )}
    </>
  );
}

// ── AI usage ─────────────────────────────────────────────────────────────────

function formatRelative(iso: string): string {
  const ts = new Date(iso).getTime();
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

function AiUsageSections() {
  const [data, setData] = useState<AiUsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    settingsService
      .getAiUsage(30)
      .then(setData)
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const title = 'AI use, last 30 days';
  const description = 'Where your tokens and spend went.';

  if (loading || failed || !data) {
    return (
      <SettingsSection title={title} description={description}>
        <SettingsCard padded={false}>
          {failed ? <LoadErrorState bare what="your AI usage" onRetry={load} /> : <SkeletonRows rows={4} rowClass="h-12" />}
        </SettingsCard>
      </SettingsSection>
    );
  }

  if (data.summary.totalCalls === 0) {
    return (
      <SettingsSection title={title} description={description}>
        <SettingsCard padded={false}>
          <EmptyBlock icon={<Activity size={20} />} title="No AI calls yet">Nothing in the last 30 days. Calls show up here as your team uses AI.</EmptyBlock>
        </SettingsCard>
      </SettingsSection>
    );
  }

  return (
    <>
      <SettingsSection title={title} description={description}>
        <div className="grid grid-cols-2 gap-3 @5xl:grid-cols-4">
          <SettingsStat label="Calls" value={data.summary.totalCalls.toLocaleString()} />
          <SettingsStat label="Tokens" value={formatTokens(data.summary.totalTokens)} />
          <SettingsStat label="Cost (USD)" value={`$${data.summary.totalCostUsd.toFixed(3)}`} />
          <SettingsStat label="Plan credits" value={data.summary.totalCredits.toLocaleString()} />
        </div>

        {data.summary.byProvider.length > 1 && (
          <SettingsCard padded={false}>
            <ul className="divide-y divide-border">
              {data.summary.byProvider.map((p) => {
                const pct = data.summary.totalTokens
                  ? Math.round((p.tokens / data.summary.totalTokens) * 100)
                  : 0;
                return (
                  <li key={p.provider} className="px-5 py-3">
                    <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-xs">
                      <span className="text-sm font-medium capitalize text-text-primary">{p.provider}</span>
                      <span className="text-text-muted">
                        {p.calls} calls · {formatTokens(p.tokens)} tokens · ${p.costUsd.toFixed(3)}
                      </span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-surface-alt">
                      <div className="h-full rounded-full bg-gold-gradient" style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </SettingsCard>
        )}
      </SettingsSection>

      <div className="py-7">
        <div className="mb-3">
          <h2 className="text-sm font-semibold text-text-primary">Recent AI calls</h2>
          <p className="mt-1 text-xs text-text-muted">
            {data.recent.length > 25 ? `The latest 25 of ${data.recent.length} calls.` : 'The latest calls.'}
          </p>
        </div>
        <SettingsCard padded={false}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-xs">
              <thead>
                <tr className={clsx(TABLE_HEAD, 'border-b border-border')}>
                  <th className="px-5 py-2.5 font-semibold">When</th>
                  <th className="px-3 py-2.5 font-semibold">Feature</th>
                  <th className="px-3 py-2.5 font-semibold">Model</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Tokens</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Cost</th>
                  <th className="px-5 py-2.5 font-semibold">Key</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.slice(0, 25).map((row, i) => (
                  <tr key={i} className="border-b border-border last:border-b-0 hover:bg-surface-alt/60">
                    <td className="whitespace-nowrap px-5 py-3 text-text-muted">{formatRelative(row.createdAt)}</td>
                    <td className="px-3 py-3 text-text-primary">{row.feature}</td>
                    <td className="px-3 py-3 font-mono text-text-muted">{row.model}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-text-primary">
                      {formatTokens(row.tokensIn + row.tokensOut)}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-text-muted">${row.costUsd.toFixed(4)}</td>
                    <td className="px-5 py-3">
                      <span className={clsx(
                        'rounded-full px-2 py-0.5 text-2xs font-medium',
                        row.keyScope === 'company' ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning',
                      )}>
                        {row.keyScope === 'company' ? 'BYOK' : 'Platform'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SettingsCard>
      </div>
    </>
  );
}

// ── Who pays for AI ──────────────────────────────────────────────────────────
function AiAccessCard({ isAdmin, provider, onSaved, refreshKey }: { isAdmin: boolean; provider: string; onSaved: () => void; refreshKey: number }) {
  const [value, setValue] = useState<AiAccessSettings | null>(null);
  const [saved, setSaved] = useState<AiAccessSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const load = () => { setError(false); settingsService.getAiAccess().then(v => { setValue(v); setSaved(v); }).catch(() => setError(true)); };
  useEffect(load, [refreshKey]);
  const save = async () => {
    if (!value) return;
    setBusy(true);
    try { const next = await settingsService.setAiAccess({ billingMode: value.billingMode, responseMode: value.responseMode }); setValue(next); setSaved(next); toast.success('AI access saved'); onSaved(); }
    catch { /* The API interceptor shows the error. */ } finally { setBusy(false); }
  };
  if (!value) {
    return (
      <SettingsCard padded={false}>
        {error ? <LoadErrorState bare what="your AI access settings" onRetry={load} /> : <SkeletonRows rows={2} rowClass="h-16" />}
      </SettingsCard>
    );
  }
  const changed = value.billingMode !== saved?.billingMode || value.responseMode !== saved?.responseMode;
  const modeModel = value.responseMode === 'custom' ? null : value.modelsByMode[provider]?.[value.responseMode];
  const options = [
    { id: 'slightor_managed' as const, label: 'Slightor managed', text: 'Use your included and granted credits. The platform supplies the provider key.' },
    { id: 'byok' as const, label: 'Bring your own key', text: 'Your provider bills you directly. A missing or invalid key stops the request; managed credits stay untouched.' },
  ];

  return (
    <SettingsCard
      footer={isAdmin && changed ? (
        <>
          <Button variant="ghost" size="sm" iconLeft={<RotateCcw size={14} />} disabled={busy} onClick={() => saved && setValue(saved)}>Discard</Button>
          <Button size="sm" iconLeft={<Save size={14} />} loading={busy} onClick={save}>Save AI access</Button>
        </>
      ) : undefined}
    >
      <div className="grid grid-cols-[repeat(auto-fit,minmax(15rem,1fr))] gap-3">
        {options.map((option) => {
          const on = value.billingMode === option.id;
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={on}
              disabled={!isAdmin || busy}
              onClick={() => setValue({ ...value, billingMode: option.id })}
              className={clsx('relative rounded-xl border p-4 text-left transition-colors disabled:cursor-default',
                on ? 'border-primary bg-primary-soft/60' : 'border-border enabled:hover:border-border-strong')}
            >
              {on && <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white"><Check size={12} /></span>}
              <span className="block pr-7 text-sm font-semibold text-text-primary">{option.label}</span>
              <span className="mt-1 block text-xs leading-relaxed text-text-muted">{option.text}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-5 max-w-xs">
        <FormField
          label="Response mode"
          htmlFor="ai-response-mode"
          hintBelow
          hint={modeModel ? `${provider} · ${modeModel}` : 'Uses the model selected below. Feature-specific model selections take priority.'}
        >
          <Select id="ai-response-mode" disabled={!isAdmin || busy} value={value.responseMode} onChange={(e) => setValue({ ...value, responseMode: e.target.value as AiAccessSettings['responseMode'] })}>
            <option value="fast">Fast</option>
            <option value="balanced">Balanced</option>
            <option value="premium">Premium</option>
            <option value="custom">Advanced · selected model</option>
          </Select>
        </FormField>
      </div>
      {!isAdmin && <p className="mt-1 text-2xs text-text-muted">Only a company admin can change this.</p>}
    </SettingsCard>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export type AiSettingsSection = 'providers' | 'languages' | 'whatsapp' | 'usage';

const SECTION_HEADER: Record<AiSettingsSection, { title: string; description: string }> = {
  providers: {
    title: 'Providers & models',
    description: 'Choose who pays for AI, set the default model your team uses, and keep your own provider keys here. This powers the copilot, template AI-assist and AI Studio.',
  },
  languages: {
    title: 'Language defaults',
    description: 'Pick which AI model answers in each customer language. Any language you do not list uses your default model.',
  },
  whatsapp: {
    title: 'WhatsApp fallback',
    description: 'Let the AI reply on WhatsApp when no scripted flow matches the message. It reads your business profile and uses your default model.',
  },
  usage: {
    title: 'Cost caps & usage',
    description: 'Set daily and monthly limits on AI spend, check your credits and see what the AI used in the last 30 days.',
  },
};

/** One page per AI area — Settings shows them as separate tabs. */
export default function AiSettings({ section = 'providers' }: { section?: AiSettingsSection }) {
  const { user } = useAuth();
  // Products (and its credentials vault) exists only for the platform owner.
  const { isPlatformOwner } = useModuleAccess();
  const isAdmin = user?.role === 'company_admin';
  const needsModels = section === 'providers' || section === 'languages';

  const [data, setData] = useState<AiModelsResponse | null>(null);
  const [keys, setKeys] = useState<AiProviderKey[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [modelsError, setModelsError] = useState(false);
  const [keysError, setKeysError] = useState(false);

  // Per-provider model drafts + which provider is selected as default (not yet saved)
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [selectedProvider, setSelectedProvider] = useState<AiProviderName>('openai');
  // The last-saved default (comes from the API)
  const savedDefaultRef = useRef<{ provider: AiProviderName; model: string }>({ provider: 'openai', model: 'gpt-4o-mini' });
  const [saving, setSaving] = useState(false);
  const [accessVersion, setAccessVersion] = useState(0);

  const seedDrafts = (d: AiModelsResponse) => {
    const next: Record<string, string> = {};
    for (const p of d.providers) {
      next[p.id] = p.id === d.default.provider
        ? d.default.model
        : (p.models.find((m) => !m.deprecated)?.id ?? p.models[0]?.id ?? '');
    }
    return next;
  };

  // `refresh` keeps the user's unsaved draft/selection (used after key changes);
  // the initial load snaps everything to the saved default.
  const loadModels = async (refresh = false) => {
    const d = await settingsService.listAiModels();
    setData(d);
    savedDefaultRef.current = d.default;
    if (refresh) {
      setDrafts((prev) => ({ ...seedDrafts(d), ...prev }));
    } else {
      setDrafts(seedDrafts(d));
      setSelectedProvider(d.default.provider);
    }
  };
  // A failed key list leaves `keys` empty so providers would look unconfigured — `keysError` lets the page say so.
  const loadKeys = () => (isAdmin
    ? settingsService.listAiKeys().then((r) => { setKeys(r); setKeysError(false); }).catch(() => { setKeys([]); setKeysError(true); })
    : Promise.resolve());

  const reload = () => {
    if (!needsModels) { setLoading(false); return; }
    setLoading(true);
    setModelsError(false);
    Promise.all([loadModels().catch(() => setModelsError(true)), section === 'providers' ? loadKeys() : Promise.resolve()]).finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, [isAdmin, needsModels, section]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keys changed → availability may have flipped; refresh both without a full-page skeleton.
  const onKeysChanged = () => { loadKeys(); loadModels(true).catch(() => undefined); };

  const selected = data ? { provider: selectedProvider, model: drafts[selectedProvider] ?? '' } : null;
  const isDirty = !!selected && (
    selected.provider !== savedDefaultRef.current.provider ||
    selected.model !== savedDefaultRef.current.model
  );

  const handleSave = async () => {
    if (!selected || !selected.model) return;
    setSaving(true);
    try {
      await settingsService.setAiDefault(selected);
      savedDefaultRef.current = selected;
      setDrafts((d) => ({ ...d }));
      setAccessVersion(v => v + 1);
      toast.success('Default AI model saved');
    } catch { /* interceptor shows error */ } finally { setSaving(false); }
  };

  const handleReset = () => {
    const s = savedDefaultRef.current;
    setSelectedProvider(s.provider);
    setDrafts((d) => ({ ...d, [s.provider]: s.model }));
  };

  const selectedLabel = data?.providers.find(p => p.id === selectedProvider)?.label ?? selectedProvider;
  const header = { eyebrow: 'AI', ...SECTION_HEADER[section] };

  if (section === 'languages') {
    return (
      <SettingsPage {...header}>
        <SettingsSection
          title="Model per language"
          description={isAdmin
            ? 'The language of each incoming message is detected automatically. Anything not listed uses your default model.'
            : 'The language of each incoming message is detected automatically. Only a company admin can change these.'}
          aside={<Link to="/settings/ai" className="text-xs font-medium text-primary hover:underline">Change the default model</Link>}
        >
          {modelsError ? (
            <SettingsCard padded={false}><LoadErrorState bare what="your AI providers" onRetry={reload} /></SettingsCard>
          ) : loading || !data ? (
            <SettingsCard padded={false}><SkeletonRows rows={3} rowClass="h-20" /></SettingsCard>
          ) : (
            <LanguagePreferencesCard providers={data.providers} isAdmin={isAdmin} />
          )}
        </SettingsSection>
      </SettingsPage>
    );
  }

  if (section === 'whatsapp') {
    return (
      <SettingsPage {...header}>
        <WhatsappAiFallbackSections isAdmin={isAdmin} />
      </SettingsPage>
    );
  }

  if (section === 'usage') {
    return (
      <SettingsPage {...header}>
        <CostCapsSection isAdmin={isAdmin} />
        <CreditsSections />
        <AiUsageSections />
      </SettingsPage>
    );
  }

  return (
    <SettingsPage {...header}>
      <SettingsSection
        title="How your company uses AI"
        description="Choose who supplies the API key and the default response mode. Slightor managed uses your credits; with your own key, your provider bills you instead."
      >
        <AiAccessCard refreshKey={accessVersion} isAdmin={isAdmin} provider={selectedProvider} onSaved={onKeysChanged} />
      </SettingsSection>

      <SettingsSection
        title="Providers and default model"
        description={isAdmin
          ? 'Make one provider your default and pick its model. Add your own API key to use a provider on your own bill.'
          : 'The provider and model your team uses by default. Only a company admin can change them or add keys.'}
        aside={(
          <div className="space-y-2 text-xs leading-relaxed text-text-muted">
            {isPlatformOwner && (
              <p>
                Keys for your own apps live in{' '}
                <Link to="/products" className="inline-flex items-center gap-0.5 font-medium text-primary hover:underline">Products → Credentials vault <ArrowUpRight size={12} /></Link>.
              </p>
            )}
            <p>
              AI behaviour for each WhatsApp number (AI chat or scripted flows, instructions, capture fields) is under{' '}
              <Link to="/whatsapp-account" className="font-medium text-primary hover:underline">WhatsApp → Account settings → Bot mode</Link>.
            </p>
          </div>
        )}
      >
        <SettingsCard
          padded={false}
          footer={!loading && data && isDirty ? (
            <>
              <p className="mr-auto min-w-0 break-words text-xs text-text-muted">
                <span className="font-medium text-warning">Unsaved change:</span>{' '}
                {selectedLabel} · <span className="break-all font-mono">{selected?.model}</span>
              </p>
              <Button variant="ghost" size="sm" iconLeft={<RotateCcw size={14} />} disabled={saving} onClick={handleReset}>Discard</Button>
              <Button size="sm" iconLeft={<Save size={14} />} loading={saving} disabled={!selected?.model} onClick={handleSave}>Save default</Button>
            </>
          ) : undefined}
        >
          {modelsError ? (
            <LoadErrorState bare what="your AI providers" onRetry={reload} />
          ) : loading || !data ? (
            <SkeletonRows rows={4} rowClass="h-24" />
          ) : (
            <>
              {isAdmin && keysError && (
                <div role="alert" className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border bg-warning-soft px-5 py-3 text-xs text-text-secondary">
                  <span>Your saved keys could not be loaded, so providers may look unconfigured.</span>
                  <button type="button" onClick={() => void loadKeys()} className="font-medium text-primary hover:underline">Try again</button>
                </div>
              )}
              <ProvidersVault
                providers={data.providers}
                keys={isAdmin ? keys : null}
                isAdmin={isAdmin}
                savedDefault={savedDefaultRef.current}
                selectedProvider={selectedProvider}
                drafts={drafts}
                onDraftModel={(p, m) => setDrafts((d) => ({ ...d, [p]: m }))}
                onMakeDefault={setSelectedProvider}
                onKeysChanged={onKeysChanged}
              />
            </>
          )}
        </SettingsCard>
      </SettingsSection>

      {/* Voice keys sit with the other provider keys; the rest has its own tab. */}
      {isAdmin && (
        <SettingsSection
          title="Voiceover"
          description="AI Studio reads scripts aloud with ElevenLabs. Add your own ElevenLabs key; usage is billed to your ElevenLabs account."
        >
          <ElevenLabsCard />
        </SettingsSection>
      )}
    </SettingsPage>
  );
}
